import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getToken, onMessage } from 'firebase/messaging';
import type { CustomerNotificationUnreadCount, ListCustomerNotificationsResponse } from '@mbbe/types';
import { getFirebaseMessaging, getFirebaseWebPushVapidKey } from '../../lib/firebase';
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsSeen,
  markAllNotificationsRead,
  markIssueNotificationsRead,
  markNotificationRead,
  registerNotificationDevice,
} from '../lib/notificationsApi';

export type NotificationFilter = 'all' | 'unread' | 'action_required';

export const notificationKeys = {
  all: ['destek-notifications'] as const,
  list: (filter: NotificationFilter) => ['destek-notifications', 'list', { filter }] as const,
  unread: ['destek-notifications', 'unread-count'] as const,
};

function nowIso(): string {
  return new Date().toISOString();
}

function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

function updateUnreadCount(current: CustomerNotificationUnreadCount | undefined, nextUnread: number) {
  return { unread: Math.max(0, nextUnread ?? current?.unread ?? 0) };
}

export function useCustomerNotifications(selectedFilter: NotificationFilter | boolean = 'all') {
  const filter: NotificationFilter =
    selectedFilter === true ? 'unread' : selectedFilter === false ? 'all' : selectedFilter;
  const queryClient = useQueryClient();
  const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>(
    () => getNotificationPermission()
  );

  const listQuery = useQuery({
    queryKey: notificationKeys.list(filter),
    queryFn: () => listNotifications({ limit: 40, filter }),
    refetchInterval: 45_000,
  });

  const unreadQuery = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: getUnreadNotificationCount,
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    queryClient.invalidateQueries({ queryKey: notificationKeys.unread });
  };

  const readMutation = useMutation({
    mutationFn: markNotificationRead,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previousLists = queryClient.getQueriesData<ListCustomerNotificationsResponse>({
        queryKey: ['destek-notifications', 'list'],
      });
      const previousUnread = queryClient.getQueryData<CustomerNotificationUnreadCount>(
        notificationKeys.unread
      );
      const removedUnreadIds = new Set<string>();
      queryClient.setQueriesData<ListCustomerNotificationsResponse>(
        { queryKey: ['destek-notifications', 'list'] },
        (current) => {
          if (!current) return current;
          const nextReadAt = nowIso();
          const items = current.items.map((item) => {
            if (item.id !== id || item.readAt) return item;
            removedUnreadIds.add(item.id);
            return { ...item, readAt: nextReadAt };
          });
          return {
            ...current,
            items,
            total: current.total,
          };
        }
      );
      queryClient.setQueryData<ListCustomerNotificationsResponse>(notificationKeys.list('unread'), (current) => {
        if (!current) return current;
        const before = current.items.length;
        const items = current.items.filter((item) => item.id !== id);
        return { ...current, items, total: Math.max(0, current.total - (before - items.length)) };
      });
      queryClient.setQueryData<CustomerNotificationUnreadCount>(notificationKeys.unread, (current) =>
        updateUnreadCount(current, (current?.unread ?? 0) - removedUnreadIds.size)
      );
      return { previousLists, previousUnread };
    },
    onError: (_error, _id, context) => {
      if (context?.previousLists) {
        for (const [key, value] of context.previousLists) {
          queryClient.setQueryData(key, value);
        }
      }
      if (context?.previousUnread) {
        queryClient.setQueryData(notificationKeys.unread, context.previousUnread);
      }
    },
    onSettled: invalidate,
  });

  const readAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previousLists = queryClient.getQueriesData<ListCustomerNotificationsResponse>({
        queryKey: ['destek-notifications', 'list'],
      });
      const previousUnread = queryClient.getQueryData<CustomerNotificationUnreadCount>(
        notificationKeys.unread
      );
      queryClient.setQueriesData<ListCustomerNotificationsResponse>(
        { queryKey: ['destek-notifications', 'list'] },
        (current) => {
          if (!current) return current;
          const nextReadAt = nowIso();
          return {
            ...current,
            items: current.items.map((item) => item.readAt ? item : { ...item, readAt: nextReadAt, seenAt: item.seenAt ?? nextReadAt }),
          };
        }
      );
      queryClient.setQueryData<ListCustomerNotificationsResponse>(notificationKeys.list('unread'), (current) =>
        current ? { ...current, items: [], total: 0 } : current
      );
      queryClient.setQueryData<CustomerNotificationUnreadCount>(notificationKeys.unread, { unread: 0 });
      return { previousLists, previousUnread };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLists) {
        for (const [key, value] of context.previousLists) {
          queryClient.setQueryData(key, value);
        }
      }
      if (context?.previousUnread) {
        queryClient.setQueryData(notificationKeys.unread, context.previousUnread);
      }
    },
    onSettled: invalidate,
  });

  const seenAllMutation = useMutation({
    mutationFn: markAllNotificationsSeen,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });
      const previousLists = queryClient.getQueriesData<ListCustomerNotificationsResponse>({
        queryKey: ['destek-notifications', 'list'],
      });
      const nextSeenAt = nowIso();
      queryClient.setQueriesData<ListCustomerNotificationsResponse>(
        { queryKey: ['destek-notifications', 'list'] },
        (current) => current ? {
          ...current,
          items: current.items.map((item) => item.seenAt ? item : { ...item, seenAt: nextSeenAt }),
        } : current
      );
      return { previousLists };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousLists) {
        for (const [key, value] of context.previousLists) {
          queryClient.setQueryData(key, value);
        }
      }
    },
    onSettled: invalidate,
  });

  const readIssueMutation = useMutation({
    mutationFn: markIssueNotificationsRead,
    onSuccess: invalidate,
  });

  const pushMutation = useMutation({
    mutationFn: async () => {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        setPushPermission('unsupported');
        return { permission: 'denied' as NotificationPermission, registered: false };
      }

      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') return { permission, registered: false };

      const messaging = await getFirebaseMessaging();
      if (!messaging) return { permission, registered: false };

      const token = await getToken(messaging, { vapidKey: getFirebaseWebPushVapidKey() });
      if (!token) return { permission, registered: false };

      await registerNotificationDevice({ token, permission: 'granted', platform: 'web' });
      return { permission, registered: true };
    },
  });

  useEffect(() => {
    setPushPermission(getNotificationPermission());
  }, []);

  useEffect(() => {
    let unsubscribe: undefined | (() => void);
    getFirebaseMessaging()
      .then((messaging) => {
        if (!messaging) return;
        unsubscribe = onMessage(messaging, invalidate);
      })
      .catch(() => {
        /* FCM is optional; polling remains the source of truth. */
      });
    return () => unsubscribe?.();
  }, [queryClient]);

  const refetch = () => {
    listQuery.refetch();
    unreadQuery.refetch();
  };

  return {
    items: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    unread: unreadQuery.data?.unread ?? 0,
    unreadCount: unreadQuery.data?.unread ?? 0,
    isLoading: listQuery.isLoading || unreadQuery.isLoading,
    isError: listQuery.isError || unreadQuery.isError,
    refetch,
    listQuery,
    unreadQuery,
    markRead: readMutation.mutateAsync,
    markAllRead: readAllMutation.mutateAsync,
    markAllSeen: seenAllMutation.mutateAsync,
    markIssueRead: readIssueMutation.mutateAsync,
    isMarkingRead: readMutation.isPending,
    isMarkingAllRead: readAllMutation.isPending,
    pushPermission,
    requestPushPermission: pushMutation.mutate,
    requestPushPermissionAsync: pushMutation.mutateAsync,
    isRequestingPushPermission: pushMutation.isPending,
    pushPermissionError: pushMutation.error,
  };
}
